let cylindoScriptPromise: Promise<unknown> | null = null;
let preconnectAdded = false;

const VIEWER_SCRIPT_URL = "https://viewer-cdn.cylindo.com/v1/index.mjs";
const THUMBNAIL_BAR_SCRIPT_URL = "https://viewer-cdn.cylindo.com/v1/bar.mjs";
const CONTENT_ORIGIN = "https://content.cylindo.com";

function addPreconnect() {
    if (preconnectAdded || typeof document === "undefined") {
        return;
    }
    preconnectAdded = true;
    const link = document.createElement("link");
    link.rel = "preconnect";
    link.href = CONTENT_ORIGIN;
    link.crossOrigin = "";
    document.head.appendChild(link);
}

function loadModuleScript(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${url}"]`) as HTMLScriptElement | null;
        if (existing) {
            if (existing.dataset.loaded === "true") {
                resolve();
                return;
            }
            existing.addEventListener("load", () => resolve(), { once: true });
            existing.addEventListener("error", () => reject(new Error(`Failed to load ${url}`)), { once: true });
            return;
        }
        const script = document.createElement("script");
        script.type = "module";
        script.src = url;
        script.async = true;
        script.onload = () => {
            script.dataset.loaded = "true";
            resolve();
        };
        script.onerror = () => reject(new Error(`Failed to load ${url}`));
        document.head.appendChild(script);
    });
}

export const loadCylindoViewerScript = (): Promise<unknown> => {
    if (typeof window === "undefined" || typeof document === "undefined") {
        return Promise.reject(new Error("Cylindo viewer is not available during SSR."));
    }

    addPreconnect();

    if (customElements.get("cylindo-viewer")) {
        return Promise.resolve();
    }

    if (cylindoScriptPromise) {
        return cylindoScriptPromise;
    }

    cylindoScriptPromise = Promise.all([
        loadModuleScript(VIEWER_SCRIPT_URL),
        loadModuleScript(THUMBNAIL_BAR_SCRIPT_URL),
    ]).catch((error) => {
        cylindoScriptPromise = null;
        throw error;
    });

    return cylindoScriptPromise;
};

// ---------------------------------------------------------------------------
// Cylindo Content API - product configuration
// ---------------------------------------------------------------------------

export interface CylindoFeatureOption {
    code: string;
    name: string;
    isDefault: boolean;
}

export interface CylindoFeature {
    code: string;
    name: string;
    options: CylindoFeatureOption[];
}

export interface CylindoProductConfiguration {
    code: string;
    features: CylindoFeature[];
}

export type CylindoErrorEvent = ErrorEvent & {
    detail?: {
        error?: string;
        errorType?: string;
    };
}

const configurationCache = new Map<string, CylindoProductConfiguration>();
const inflightRequests = new Map<string, Promise<CylindoProductConfiguration>>();

function configCacheKey(customerId: string, productCode: string): string {
    return `${customerId}/${productCode}`;
}

export function getCachedCylindoConfiguration(
    customerId: string,
    productCode: string,
): CylindoProductConfiguration | null {
    return configurationCache.get(configCacheKey(customerId, productCode)) ?? null;
}

/**
 * Fetches the product configuration from the Cylindo Content API.
 * Returns a cached result when available. De-duplicates in-flight requests.
 */
export function fetchCylindoConfiguration(
    customerId: string,
    productCode: string,
): Promise<CylindoProductConfiguration> {
    const key = configCacheKey(customerId, productCode);

    const cached = configurationCache.get(key);
    if (cached) {
        return Promise.resolve(cached);
    }

    const inflight = inflightRequests.get(key);
    if (inflight) {
        return inflight;
    }

    const encodedCode = encodeURIComponent(productCode);
    const url = `${CONTENT_ORIGIN}/api/v2/${encodeURIComponent(customerId)}/products/${encodedCode}/configuration`;

    const request = fetch(url)
        .then(response => {
            if (!response.ok) {
                throw new Error(`Cylindo configuration request failed: ${response.status}`);
            }
            return response.json() as Promise<CylindoProductConfiguration>;
        })
        .then(config => {
            configurationCache.set(key, config);
            inflightRequests.delete(key);
            return config;
        })
        .catch(error => {
            inflightRequests.delete(key);
            throw error;
        });

    inflightRequests.set(key, request);
    return request;
}

export interface CylindoFeatureValidationResult {
    validFeatures: Record<string, string>;
    droppedFeatures: string[];
    hasWarning: boolean;
}

/**
 * Validates requested feature:option pairs against a Cylindo product
 * configuration. Returns only the pairs where both the feature code and the
 * option code exist in the configuration.
 */
export function validateCylindoFeatures(
    requested: Record<string, string>,
    config: CylindoProductConfiguration,
): CylindoFeatureValidationResult {
    const validFeatures: Record<string, string> = {};
    const droppedFeatures: string[] = [];

    for (const [featureCode, optionCode] of Object.entries(requested)) {
        const feature = config.features.find(f => f.code.toLowerCase() === featureCode.toLowerCase());
        if (!feature) {
            droppedFeatures.push(featureCode);
            continue;
        }
        const option = feature.options.find(o => o.code.toLowerCase() === optionCode.toLowerCase());
        if (!option) {
            droppedFeatures.push(featureCode);
            continue;
        }
        validFeatures[feature.code] = option.code;
    }

    // If no requested pairs are valid, fall back to configured defaults.
    if (Object.keys(validFeatures).length === 0) {
        for (const feature of config.features) {
            const fallbackOption = feature.options.find(o => o.isDefault) ?? feature.options[0];
            if (fallbackOption) {
                validFeatures[feature.code] = fallbackOption.code;
            }
        }
    }

    return {
        validFeatures,
        droppedFeatures,
        hasWarning: droppedFeatures.length > 0,
    };
}

// ---------------------------------------------------------------------------
// Step-by-step fallback when the viewer cannot render the requested features
// ---------------------------------------------------------------------------

const BASE_COVER_FEATURE = "BASECOVER";
export const CYLINDO_FALLBACK_STEP_COUNT = 4;

function findFeatureKey(features: Record<string, string>, featureCode: string): string | undefined {
    return Object.keys(features).find(key => key.toLowerCase() === featureCode.toLowerCase());
}

function findConfigFeature(config: CylindoProductConfiguration, featureCode: string): CylindoFeature | undefined {
    return config.features.find(f => f.code.toLowerCase() === featureCode.toLowerCase());
}

function resetFeature(
    features: Record<string, string>,
    config: CylindoProductConfiguration,
    featureCode: string,
    optionCode: string,
) {
    const key = findFeatureKey(features, featureCode);
    const option = key ? findConfigFeature(config, featureCode)?.options.find(o => o.code.toLowerCase() === optionCode.toLowerCase()) : undefined;
    if (key && option) {
        features[key] = option.code;
    }
}

/**
 * Applies fallback steps 1..`step` cumulatively:
 * 1. OPTIONALWELT -> NO, 2. OPTIONALSEAT -> MULTI, 3. drop everything except the base cover,
 * 4. base cover -> first Cylindo option. Steps only apply when the feature/option exists in Cylindo.
 */
export function applyCylindoFallbackStep(
    features: Record<string, string>,
    step: number,
    config: CylindoProductConfiguration | null,
): Record<string, string> {
    if (!features || step <= 0 || !config) {
        return features;
    }

    let result: Record<string, string> = { ...features };
    resetFeature(result, config, "OPTIONALWELT", "NO");
    if (step >= 2) {
        resetFeature(result, config, "OPTIONALSEAT", "MULTI");
    }
    if (step >= 3) {
        const baseCoverKey = findFeatureKey(result, BASE_COVER_FEATURE);
        result = baseCoverKey ? { [baseCoverKey]: result[baseCoverKey] } : {};
    }
    if (step >= 4) {
        const baseCover = findConfigFeature(config, BASE_COVER_FEATURE);
        if (baseCover?.options.length) {
            result = { [findFeatureKey(result, BASE_COVER_FEATURE) ?? baseCover.code]: baseCover.options[0].code };
        }
    }
    return result;
}

function featuresSignature(features: Record<string, string>): string {
    return Object.keys(features)
        .map(key => `${key.toUpperCase()}:${features[key]}`)
        .sort()
        .join(",");
}

/** Next step that actually changes the rendered features, or null when all steps are exhausted. */
export function getNextCylindoFallbackStep(
    features: Record<string, string>,
    currentStep: number,
    config: CylindoProductConfiguration,
): number | null {
    const current = featuresSignature(applyCylindoFallbackStep(features, currentStep, config));
    for (let step = currentStep + 1; step <= CYLINDO_FALLBACK_STEP_COUNT; step++) {
        if (featuresSignature(applyCylindoFallbackStep(features, step, config)) !== current) {
            return step;
        }
    }
    return null;
}

// ---------------------------------------------------------------------------
// Cylindo Content API - thumbnail URL builder
// ---------------------------------------------------------------------------

/** Shape of an entry in the `viewer.items` array exposed by `<cylindo-viewer>`. */
export interface CylindoViewerItem {
    type: "360" | "360StaticFrame" | "custom" | "model" | "studio" | "swatch" | "dimensionShot";
    frame?: number;
    frames?: number[];
    slot?: string;
    thumbnailSrc?: string;
    src?: string;
    zoomSrc?: string;
    alt?: string;
    canBeZoomedIn?: boolean;
    features?: Record<string, string>;
    interactionPrompt?: string;
    dimensionCode?: string;
    dimensionLabelUnit?: string;
}

/**
 * Builds a Cylindo Content API thumbnail URL.
 *
 * Pattern mirrors the backend `CylindoImageService.BuildImageUrl`. The `size`
 * argument controls the rendered pixel dimension and defaults to `105` for the
 * thumbnail use case; callers that need higher resolution (e.g. the print spec
 * sheet) can pass a larger value.
 *
 * Example:
 * ```
 * https://content.cylindo.com/api/v2/4984/products/GISELLE%20SOFA/frames/1/GISELLE%20SOFA.webp?size=105&background=f8f7f5&feature=BASECOVER:VALUE
 * ```
 */
export function buildCylindoThumbnailUrl(
    accountId: string,
    productCode: string,
    frame: number,
    features?: Record<string, string>,
    backgroundColor?: string,
    size = 105,
): string {
    const encodedCode = encodeURIComponent(productCode);
    const baseUrl = `${CONTENT_ORIGIN}/api/v2/${encodeURIComponent(accountId)}/products/${encodedCode}/frames/${frame}/${encodedCode}.webp`;

    const params: string[] = [`size=${size}`];

    if (backgroundColor) {
        params.push(`background=${encodeURIComponent(backgroundColor)}`);
    }

    if (features) {
        const sortedKeys = Object.keys(features).sort();
        for (const key of sortedKeys) {
            const value = features[key];
            if (key && value) {
                params.push(`feature=${encodeURIComponent(key)}:${encodeURIComponent(value)}`);
            }
        }
    }

    params.push('ignoreunknownfeatures=true');

    return `${baseUrl}?${params.join("&")}`;
}

/**
 * Merges the given Cylindo `features` into an existing Cylindo content image URL,
 * overriding any matching `feature=KEY:VALUE` query params and preserving all
 * other params (size, background, and unmatched features). Used to re-skin a
 * related product's default preview image with the current configurator
 * selections.
 *
 * Returns `url` unchanged when:
 * - `features` is empty/nullish, or
 * - `url` is falsy, or
 * - `url` is not a Cylindo content URL.
 *
 * Pure and SSR-safe (no `window`/DOM usage); uses the `URL` / `URLSearchParams`
 * browser APIs to parse the query string.
 */
export function applyCylindoFeaturesToUrl(
    url: string | undefined | null,
    features: Record<string, string> | null | undefined,
): string {
    if (!url) {
        return url ?? "";
    }

    if (!features || Object.keys(features).length === 0) {
        return url;
    }

    if (url.indexOf(CONTENT_ORIGIN) !== 0) {
        return url;
    }

    const parsedUrl = new URL(url);

    if (!parsedUrl.search) {
        return url;
    }

    // Preserve non-feature params; collect existing features by key.
    const otherParams: Array<[string, string]> = [];
    const featureMap = new Map<string, string>();

    parsedUrl.searchParams.forEach((value, key) => {
        if (key === "feature") {
            const sepIndex = value.indexOf(":");
            if (sepIndex < 0) {
                otherParams.push([key, value]);
                return;
            }

            const featureKey = decodeURIComponent(value.slice(0, sepIndex)).toUpperCase();
            const featureValue = value.slice(sepIndex + 1);
            featureMap.set(featureKey, featureValue);
        } else {
            otherParams.push([key, value]);
        }
    });

    // Override matching keys (Cylindo feature codes are upper-case by convention).
    for (const key of Object.keys(features)) {
        const value = features[key];
        if (key && value) {
            featureMap.set(key.toUpperCase(), value);
        }
    }

    const featureParams = Array.from(featureMap.keys())
        .sort()
        .map(key => `feature=${encodeURIComponent(key)}:${encodeURIComponent(featureMap.get(key) ?? "")}`);

    const allParams = otherParams
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .concat(featureParams);

    return `${parsedUrl.origin}${parsedUrl.pathname}?${allParams.join("&")}${parsedUrl.hash}`;
}
